import { useEffect, useState } from 'react';
import type { BallState, RoundState } from '@bingo/shared';
import { serverNow } from './clock.js';

/**
 * Separa o que o público pode VER do que o servidor já sabe.
 *
 * O bug que isto resolve: o número aparecia no painel B-I-N-G-O, no contador e
 * nas "últimas bolas" **antes** de a bola grande terminar de girar — o telão
 * entregava a resposta antes do suspense.
 *
 * A correção não é sequenciar animações à mão. É derivar o estado visível do
 * relógio: uma bola com `revealAt` no futuro simplesmente **não existe** para
 * a tela. Com isso, três coisas caem de graça:
 *
 *  · tudo o que mostra o número muda no MESMO instante (painel, contador,
 *    últimas bolas, "Bola N"), porque todos leem a mesma lista;
 *  · **reconexão no meio do suspense** funciona sem código especial — o
 *    `state:sync` traz `revealAt`, e o telão simplesmente gira pelo tempo que
 *    falta;
 *  · **desfazer durante o suspense** cancela a revelação sozinho: a bola some
 *    da lista e não há o que revelar.
 */
export interface RevealedRound {
  /** Bolas que o público já pode ver, em ordem de sorteio. */
  visibleBalls: BallState[];
  /** Bola girando agora, se houver. */
  pending: BallState | null;
  /** Última bola revelada — a da bola grande. */
  lastRevealed: BallState | null;
}

export function useRevealedRound(round: RoundState | null): RevealedRound {
  // Um contador para forçar o recálculo no instante da revelação. O estado
  // de verdade é a rodada + o relógio; isto só acorda o componente.
  const [, tick] = useState(0);

  const balls = round?.balls ?? [];
  const now = serverNow();

  const visibleBalls = balls.filter((ball) => Date.parse(ball.revealAt) <= now);
  const pending = balls.find((ball) => Date.parse(ball.revealAt) > now) ?? null;

  const pendingAt = pending?.revealAt ?? null;

  useEffect(() => {
    if (!pendingAt) return;

    // +30ms de folga: acordar exatamente no limite às vezes recalcula um
    // milissegundo antes e a bola ficaria presa mais um ciclo.
    const delay = Math.max(0, Date.parse(pendingAt) - serverNow()) + 30;
    const timer = window.setTimeout(() => tick((value) => value + 1), delay);

    return () => window.clearTimeout(timer);
  }, [pendingAt]);

  return {
    visibleBalls,
    pending,
    lastRevealed: visibleBalls.at(-1) ?? null,
  };
}
